/**
 * Dark sidebar of the back-office (Admin-Dashboard / AF-Vue / FS-Vue / TD-Console / AG-* / AC-Cockpit):
 * 248 px, #1B1C31; brand row with collapse button, « ESPACE CABINET » chip (lime dot), search (38 px),
 * sections (10 px uppercase #8387A0), rows 36 px radius 9 13 px #A8ABC2 (active rgba(255,255,255,.10) white 600),
 * sub-items with a left rule, counters #5B4FE0, footer « Déconnexion » + user block with its menu.
 * Collapsed: 72 px, icons only (40 px rows, badges as corner pills). Below 1024 px it lives in a 264 px drawer.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronsLeft, ChevronsRight, LogOut, X } from "lucide-react";
import { useApiQuery } from "@/api/hooks";
import { LogoSymbol } from "@/brand/logo";
import { ROLE_LABEL, ROLE_STYLE } from "@/contracts/permissions";
import { shellApi, type InboxSummary } from "@/contracts/shell";
import type { StaffSession } from "@/contracts/staff-auth";
import { cn } from "@/lib/cn";
import { initials } from "@/lib/format";
import { navFor, type NavItem, type Space } from "./nav";
import { SidebarSearch } from "./search";
import { UserMenu } from "./user-menu";

interface SidebarProps {
  session: StaffSession;
  space: Space;
  navKey: string | null;
  collapsed: boolean;
  onToggle?: () => void;
  /** Drawer mode (< 1024 px): close button instead of collapse, closes after navigation. */
  onClose?: () => void;
  onLogout: () => void;
}

export function Sidebar({ session, space, navKey, collapsed, onToggle, onClose, onLogout }: SidebarProps) {
  const { user } = session;
  const sections = navFor(space, user.role);
  const summary = useApiQuery(shellApi.inboxSummary, undefined, { refetchInterval: 30_000, retry: false, staleTime: 20_000 }).data;
  const drawer = !!onClose;
  // Keep the active row in view: deep sub-menus (Finance › Compta mensuelle) sit below the fold of the scrolling nav.
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    navRef.current?.querySelector<HTMLElement>('[aria-current="page"]')?.scrollIntoView?.({ block: "nearest" });
  }, [navKey, space]);
  return (
    <div className={cn("flex h-full flex-col bg-shell text-white", collapsed ? "w-[72px]" : drawer ? "w-[264px]" : "w-[248px]")}>
      <div className={cn("flex items-center gap-2 pb-2 pt-3.5", collapsed ? "flex-col px-0" : "pl-3.5 pr-3")}>
        <Link to="/" className="flex min-w-0 items-center gap-2" aria-label="Melrys · accueil de la console" onClick={onClose}>
          <LogoSymbol height={22} />
          {!collapsed && <span className="text-[14.5px] font-bold tracking-[-0.02em]">Melrys</span>}
        </Link>
        {drawer ? (
          <button type="button" onClick={onClose} aria-label="Fermer le menu" className="ml-auto inline-flex size-8 items-center justify-center rounded-[9px] border border-white/10 bg-white/[.07] text-shell-text hover:text-white">
            <X className="size-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            aria-label={collapsed ? "Déplier le menu" : "Replier le menu"}
            title={collapsed ? "Déplier le menu" : "Replier le menu"}
            className={cn("inline-flex size-8 items-center justify-center rounded-[9px] border border-white/10 bg-white/[.07] text-shell-text hover:text-white", !collapsed && "ml-auto")}
          >
            {collapsed ? <ChevronsRight className="size-4" /> : <ChevronsLeft className="size-4" />}
          </button>
        )}
      </div>
      {!collapsed && (
        <div className="px-3.5 pb-3">
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/[.08] px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.06em]">
            <span className="size-1.5 rounded-full bg-lime" aria-hidden />
            Espace cabinet
          </span>
        </div>
      )}
      <SidebarSearch collapsed={collapsed} onExpand={onToggle} onNavigate={onClose} />
      <nav ref={navRef} aria-label="Menu principal" className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain pb-3 pt-1", collapsed ? "px-2" : "px-2")}>
        {sections.map((sec, i) => (
          <div key={sec.label} className={cn("flex flex-col gap-0.5", i > 0 && (collapsed ? "mt-1.5 border-t border-white/[.08] pt-1.5" : "mt-[18px]"))}>
            {!collapsed && <div className="px-3 py-[5px] text-[10px] font-bold uppercase tracking-[0.07em] text-shell-muted">{sec.label}</div>}
            {sec.items.map((item) => (
              <NavRow key={item.key} item={item} active={navKey} collapsed={collapsed} summary={summary} onNavigate={onClose} />
            ))}
          </div>
        ))}
      </nav>
      <div className="border-t border-white/[.08] p-2">
        <button
          type="button"
          onClick={onLogout}
          title={collapsed ? "Déconnexion" : undefined}
          className={cn("flex h-9 w-full items-center gap-2.5 rounded-[9px] px-3 text-[13px] font-medium text-shell-text hover:bg-white/[.06] hover:text-white", collapsed && "justify-center px-0")}
        >
          <LogOut className="size-[17px] shrink-0" strokeWidth={1.9} />
          {!collapsed && "Déconnexion"}
        </button>
      </div>
      <UserBlock session={session} collapsed={collapsed} onLogout={onLogout} onNavigate={onClose} />
    </div>
  );
}

function Count({ n, collapsed }: { n: number; collapsed: boolean }) {
  if (!n) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-full bg-primary-500 font-bold text-white",
        collapsed ? "absolute right-[5px] top-[3px] h-4 min-w-4 px-1 text-[9px] font-extrabold" : "ml-auto h-5 min-w-5 px-1.5 text-[10.5px]",
      )}
    >
      {n > 99 ? "99+" : n}
    </span>
  );
}

function NavRow({ item, active, collapsed, summary, onNavigate, child }: { item: NavItem; active: string | null; collapsed: boolean; summary?: InboxSummary; onNavigate?: () => void; child?: boolean }) {
  const isActive = active === item.key;
  const childActive = !!item.children?.some((c) => c.key === active);
  // Open while one of its children is the current page, unless the user folded it.
  const [toggled, setOpen] = useState<boolean | null>(null);
  const open = toggled ?? childActive;
  const n = item.badge ? Number(summary?.[item.badge] ?? 0) : 0;
  const Icon = item.icon;
  const rowClass = cn(
    "relative flex items-center gap-2.5 rounded-[9px] text-[13px] font-medium text-shell-text transition-colors hover:text-white",
    collapsed ? "h-10 justify-center px-0" : "min-h-9 px-3 py-1.5 leading-tight",
    (isActive || (collapsed && childActive)) && "bg-white/10 font-semibold text-white",
  );
  if (item.children && !collapsed) {
    return (
      <div>
        <div className={cn(rowClass, "pr-1")}>
          <Link to={item.to} onClick={onNavigate} title={item.title} className="flex min-w-0 flex-1 items-center gap-2.5 self-stretch">
            <Icon className="size-[17px] shrink-0" strokeWidth={1.9} />
            <span className="min-w-0 break-words">{item.label}</span>
          </Link>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={open ? `Replier ${item.label}` : `Déplier ${item.label}`}
            className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-shell-muted hover:text-white"
          >
            <ChevronDown className={cn("size-3.5 transition-transform", open && "rotate-180")} />
          </button>
        </div>
        {open && (
          <div className="ml-5 mt-0.5 flex flex-col gap-0.5 border-l border-white/10 pl-1.5">
            {item.children.map((c) => (
              <NavRow key={c.key} item={c} active={active} collapsed={false} summary={summary} onNavigate={onNavigate} child />
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <Link to={item.to} onClick={onNavigate} aria-current={isActive ? "page" : undefined} title={collapsed ? item.label : item.title} className={rowClass}>
      {!child && <Icon className="size-[17px] shrink-0" strokeWidth={1.9} />}
      {!collapsed && <span className="min-w-0 break-words">{item.label}</span>}
      <Count n={n} collapsed={collapsed} />
    </Link>
  );
}

function UserBlock({ session, collapsed, onLogout, onNavigate }: { session: StaffSession; collapsed: boolean; onLogout: () => void; onNavigate?: () => void }) {
  const { user } = session;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const name = `${user.firstName} ${user.lastName}`;
  return (
    <div ref={ref} className="relative border-t border-white/[.08]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menu utilisateur"
        className={cn("flex w-full items-center gap-[9px] py-3 text-left hover:bg-white/[.04]", collapsed ? "justify-center px-0" : "px-4")}
      >
        <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold" style={{ background: ROLE_STYLE[user.role].avatar }} aria-hidden>
          {initials(name)}
        </span>
        {!collapsed && (
          <span className="min-w-0 flex-1">
            <span className="block break-words text-[12.5px] font-semibold leading-snug">{name}</span>
            <span className="block break-words text-[10.5px] text-shell-muted">{user.title ?? ROLE_LABEL[user.role]}</span>
          </span>
        )}
      </button>
      {open && <UserMenu session={session} anchor={ref} onClose={() => setOpen(false)} onLogout={onLogout} onNavigate={onNavigate} />}
    </div>
  );
}
